import { RiArrowLeftSLine, RiArrowRightSLine } from '@remixicon/react';
import { cva, type VariantProps } from 'class-variance-authority';
import React from 'react';
import {
  Button,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateInput,
  DateRangePicker,
  DateRangePickerProps,
  DateSegment,
  DateValue,
  Group,
  Heading,
  Label,
  RangeCalendar,
} from 'react-aria-components';
import { cn } from './utils';

const wrapperVariants = cva(
  'inline-flex items-center bg-white border transition-all duration-150 cursor-pointer overflow-hidden rounded-[10px]',
  {
    variants: {
      variant: { default: 'border-neutral-5 hover:border-neutral-6', error: 'border-red-text' },
      size: {
        sm: 'h-[32px] px-2.5 gap-1.5 text-14',
        md: 'h-[40px] px-3 gap-2 text-14',
        lg: 'h-[48px] px-4 gap-2.5 text-16',
      },
      isFocusWithin: { true: '', false: '' },
      isDisabled: { true: 'opacity-60 pointer-events-none bg-neutral-2', false: '' },
    },
    compoundVariants: [
      { variant: 'default', isFocusWithin: true, className: 'border-brand-6 ring-2 ring-ring-primary' },
      { variant: 'error', isFocusWithin: true, className: 'ring-2 ring-ring-destructive' },
    ],
    defaultVariants: { variant: 'default', size: 'md', isFocusWithin: false, isDisabled: false },
  }
);

const labelVariants = cva('text-neutral-text font-medium', {
  variants: { size: { sm: 'text-13', md: 'text-14', lg: 'text-14' } },
  defaultVariants: { size: 'md' },
});

type AgDateRangePickerVariantProps = VariantProps<typeof wrapperVariants>;

export interface AgDateRangePickerProps extends Omit<DateRangePickerProps<DateValue>, 'children'> {
  label?: string;
  startLabel?: string;
  endLabel?: string;
  isRequired?: boolean;
  size?: AgDateRangePickerVariantProps['size'];
  className?: string;
}

const segmentClassName = cn(
  'px-px tabular-nums outline-none rounded',
  'focus:bg-ring-primary focus:text-neutral-text',
  'data-placeholder:text-neutral-alpha-7'
);

const calendarCellClassName = cn(
  'flex items-center justify-center w-10 h-9 text-14 text-neutral-alpha-9 rounded outline-none cursor-pointer',
  'hover:bg-neutral-2 transition-colors',
  'focus:ring-2 focus:ring-ring-primary focus:ring-inset',
  // In-range (middle) cells: gray bg, no rounding
  'data-selected:bg-neutral-3 data-selected:rounded-none',
  // Start cell: white bg, green border, rounded left only
  'data-selection-start:bg-white data-selection-start:border data-selection-start:border-brand-6 data-selection-start:rounded-l-md data-selection-start:rounded-r-none',
  // End cell: white bg, green border, rounded right only
  'data-selection-end:bg-white data-selection-end:border data-selection-end:border-brand-6 data-selection-end:rounded-r-md data-selection-end:rounded-l-none',
  'data-today:text-brand-text data-today:bg-brand-2 data-today:[&:not([data-selected])]:rounded-md',
  'data-outside-month:text-neutral-alpha-5',
  'data-disabled:opacity-50 data-disabled:cursor-not-allowed',
  'data-unavailable:line-through data-unavailable:text-neutral-alpha-5'
);

export function AgDateRangePicker({
  label,
  startLabel,
  endLabel,
  isRequired,
  size = 'md',
  className,
  minValue,
  maxValue,
  isDateUnavailable,
  ...props
}: AgDateRangePickerProps) {
  const [calendarOpen, setCalendarOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const startGroupRef = React.useRef<HTMLDivElement>(null);
  const endGroupRef = React.useRef<HTMLDivElement>(null);

  // Open calendar on mousedown (capture phase) so it works even when clicking segments
  React.useEffect(() => {
    const open = () => setCalendarOpen(true);
    const els = [startGroupRef.current, endGroupRef.current];
    els.forEach((el) => el?.addEventListener('mousedown', open, true));
    return () => els.forEach((el) => el?.removeEventListener('mousedown', open, true));
  }, []);

  // Close calendar on outside click
  React.useEffect(() => {
    if (!calendarOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setCalendarOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [calendarOpen]);

  // Open on focus, close on blur (with delay so calendar clicks register)
  const blurTimeout = React.useRef<ReturnType<typeof setTimeout>>();
  const handleContainerFocus = () => {
    clearTimeout(blurTimeout.current);
    setCalendarOpen(true);
  };
  const handleContainerBlur = () => {
    blurTimeout.current = setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setCalendarOpen(false);
      }
    }, 0);
  };

  return (
    <div ref={containerRef} className="relative" onFocus={handleContainerFocus} onBlur={handleContainerBlur}>
      <DateRangePicker
        className={cn('flex flex-col gap-2', className)}
        isRequired={isRequired}
        minValue={minValue}
        maxValue={maxValue}
        isDateUnavailable={isDateUnavailable}
        {...props}
      >
        {({ isFocusWithin, isDisabled, isInvalid }) => (
          <>
            {label && (
              <Label className={cn(labelVariants({ size }))}>
                {label}
                {isRequired && <span className="text-red-text ml-2">*</span>}
              </Label>
            )}
            <div className="flex items-end gap-2">
              <div className="flex flex-col gap-1">
                {startLabel && <span className={cn(labelVariants({ size }), 'text-12')}>{startLabel}</span>}
                <Group
                  ref={startGroupRef}
                  className={cn(
                    wrapperVariants({ variant: isInvalid ? 'error' : 'default', size, isFocusWithin, isDisabled })
                  )}
                >
                  <DateInput slot="start" className="flex flex-1 items-center">
                    {(segment) => <DateSegment segment={segment} className={segmentClassName} />}
                  </DateInput>
                </Group>
              </div>
              <div className="flex flex-col gap-1">
                {endLabel && <span className={cn(labelVariants({ size }), 'text-12')}>{endLabel}</span>}
                <Group
                  ref={endGroupRef}
                  className={cn(
                    wrapperVariants({ variant: isInvalid ? 'error' : 'default', size, isFocusWithin, isDisabled })
                  )}
                >
                  <DateInput slot="end" className="flex flex-1 items-center">
                    {(segment) => <DateSegment segment={segment} className={segmentClassName} />}
                  </DateInput>
                </Group>
              </div>
            </div>
          </>
        )}
      </DateRangePicker>

      {calendarOpen && (
        <div className="absolute top-full right-0 z-50 mt-2 bg-white border border-neutral-5 rounded-lg shadow-lg p-3 animate-in fade-in-0 zoom-in-95">
          <RangeCalendar
            className="w-70 outline-none"
            value={props.value}
            minValue={minValue}
            maxValue={maxValue}
            isDateUnavailable={isDateUnavailable}
            onChange={(range) => {
              props.onChange?.(range);
              setCalendarOpen(false);
            }}
          >
            <header className="flex items-center justify-between mb-2">
              <Button
                slot="previous"
                excludeFromTabOrder
                className="p-4 rounded hover:bg-neutral-2 transition-colors outline-none focus:ring-2 focus:ring-ring-primary"
              >
                <RiArrowLeftSLine className="h-5 w-5 text-neutral-text" />
              </Button>
              <Heading className="text-14 font-medium text-neutral-text" />
              <Button
                slot="next"
                excludeFromTabOrder
                className="p-4 rounded hover:bg-neutral-2 transition-colors outline-none focus:ring-2 focus:ring-ring-primary"
              >
                <RiArrowRightSLine className="h-5 w-5 text-neutral-text" />
              </Button>
            </header>
            <CalendarGrid className="w-full border-collapse">
              <CalendarGridHeader>
                {(day) => (
                  <CalendarHeaderCell className="text-12 font-medium text-neutral-alpha-7 pb-2 w-10">
                    {day}
                  </CalendarHeaderCell>
                )}
              </CalendarGridHeader>
              <CalendarGridBody>
                {(date) => <CalendarCell date={date} className={calendarCellClassName} />}
              </CalendarGridBody>
            </CalendarGrid>
          </RangeCalendar>
        </div>
      )}
    </div>
  );
}

AgDateRangePicker.displayName = 'AgDateRangePicker';
